import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";

const PhysioDashboard = () => {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId, first_name, last_name } = state;
  const isPhysioUser = role === "15";
  const { branch, setBranch, requiredBranch } = useDashboardBranch(role, branch_id, {
    allowAll: false,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [weekStart, setWeekStart] = useState(moment().startOf("isoWeek"));

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days")),
    [weekStart]
  );

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredBranch, weekStart]);

  const fetchDashboard = async () => {
    if (!requiredBranch) return;
    setLoading(true);
    try {
      const res = await UserServices.getPhysioDashboard({
        branch_id: requiredBranch,
        physio_id: isPhysioUser ? UId : undefined,
        week_start: weekStart.format("YYYY-MM-DD"),
      });
      if (res.status === 200 && res.data.status) {
        setData(res.data.data || null);
      } else {
        setData(null);
      }
    } catch (err) {
      console.log(err);
      setData(null);
    }
    setLoading(false);
  };

  const card = data?.cards || {};
  const appointmentStats = card.appointments || {};
  const prescriptionStats = card.prescriptions || {};
  const referralStats = card.referrals || {};
  const responseStats = card.responses || {};
  const gxClients = card.gx_clients || 0;
  const weekAppointments = data?.week_appointments || [];
  const upcomingAppointments = data?.upcoming_appointments || [];
  const recentPrescriptions = data?.recent_prescriptions || [];

  const dayAppointments = (day) => {
    const dateStr = day.format("YYYY-MM-DD");
    return weekAppointments
      .filter((row) => {
        if (!row.appointment_date) return false;
        return moment(row.appointment_date).format("YYYY-MM-DD") === dateStr;
      })
      .sort((a, b) => String(a.slot_start || "").localeCompare(String(b.slot_start || "")));
  };

  const slotRange = (row) => {
    const start = row.slot_start ? String(row.slot_start).substring(0, 5) : "";
    const end = row.slot_end ? String(row.slot_end).substring(0, 5) : "";
    if (!start) return "—";
    return `${start} - ${end || ""}`;
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="row mb-4 align-items-end">
          <div className="col-md-8">
            <div className="page-heading">
              <h3>
                <i className="bi bi-heart-pulse-fill me-2"></i>Physio Dashboard
              </h3>
              <p className="text-muted mb-0">
                Welcome back, {first_name} {last_name}!
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label small mb-1">Branch</label>
            <DashboardBranchFilter
              role={role}
              loginBranchId={branch_id}
              value={branch}
              onChange={setBranch}
              allowAll={false}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            <div className="row g-3 mb-4">
              {[
                { label: "Total Appointments", value: appointmentStats.total || 0, color: "primary", icon: "bi-calendar-check" },
                { label: "Today", value: appointmentStats.today || 0, color: "success", icon: "bi-calendar-day" },
                { label: "Upcoming", value: appointmentStats.upcoming || 0, color: "info", icon: "bi-clock-history" },
                { label: "GX Clients", value: gxClients || 0, color: "warning", icon: "bi-people-fill" },
              ].map((item, idx) => (
                <div key={idx} className="col-12 col-sm-6 col-xl-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="flex-grow-1">
                        <p className="mb-0 text-muted small">{item.label}</p>
                        <h4 className="mb-0 fw-bold">{item.value}</h4>
                      </div>
                      <div className={`widget-icon bg-${item.color} bg-gradient text-white rounded-3`}>
                        <i className={`bi ${item.icon} fs-4`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom">
                    <h6 className="mb-0">Prescriptions</h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total</span>
                      <strong>{prescriptionStats.total || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>This Month</span>
                      <strong>{prescriptionStats.this_month || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>This Week</span>
                      <strong>{prescriptionStats.this_week || 0}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom">
                    <h6 className="mb-0">Daily Referrals</h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Rows</span>
                      <strong>{referralStats.total_records || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Referred</span>
                      <strong>{referralStats.total_referred || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>This Week Referred</span>
                      <strong>{referralStats.this_week_referred || 0}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom">
                    <h6 className="mb-0">Client Responses</h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total</span>
                      <strong>{responseStats.total || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>This Week</span>
                      <strong>{responseStats.this_week || 0}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">
                  <i className="bi bi-calendar-week me-2"></i>
                  {weekStart.format("DD MMM")} - {weekStart.clone().endOf("isoWeek").format("DD MMM YYYY")}
                </h5>
                <div className="btn-group btn-group-sm">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setWeekStart((w) => w.clone().subtract(1, "week"))}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-outline-primary px-3"
                    onClick={() => setWeekStart(moment().startOf("isoWeek"))}
                  >
                    Today
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setWeekStart((w) => w.clone().add(1, "week"))}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
              <div className="card-body p-2" style={{ overflowX: "auto" }}>
                <div className="d-flex gap-2" style={{ minWidth: 680 }}>
                  {weekDays.map((day) => {
                    const entries = dayAppointments(day);
                    const isToday = day.isSame(moment(), "day");
                    return (
                      <div
                        key={day.format("YYYY-MM-DD")}
                        className={`flex-fill rounded-3 border ${isToday ? "border-primary border-2" : "border-secondary border-opacity-25"}`}
                        style={{ minWidth: 92 }}
                      >
                        <div className={`text-center py-2 px-1 ${isToday ? "bg-primary text-white" : "bg-light text-secondary"}`}>
                          <div className="small fw-semibold text-uppercase">{day.format("ddd")}</div>
                          <div className="fw-bold" style={{ fontSize: 22, lineHeight: 1.1 }}>{day.format("D")}</div>
                          <div className="small opacity-75">{day.format("MMM")}</div>
                          {entries.length > 0 ? (
                            <span className={`badge rounded-pill mt-1 ${isToday ? "bg-white text-primary" : "bg-primary"}`}>
                              {entries.length}
                            </span>
                          ) : null}
                        </div>
                        <div className="p-1" style={{ minHeight: 95, maxHeight: 310, overflowY: "auto" }}>
                          {entries.length === 0 ? (
                            <div className="text-center text-muted py-4">-</div>
                          ) : (
                            entries.map((row) => (
                              <div key={row.id} className="rounded-2 mb-1 p-1" style={{ background: "#f5f5f5", borderLeft: "3px solid #6f42c1", fontSize: 11 }}>
                                <div className="fw-bold" style={{ color: "#6f42c1", fontSize: 10 }}>
                                  <i className="bi bi-clock me-1"></i>{slotRange(row)}
                                </div>
                                <div className="fw-semibold text-dark text-truncate" title={row.client_name || ""}>
                                  {row.client_name || "Client"}
                                </div>
                                <div className="text-muted text-truncate" style={{ fontSize: 10 }}>
                                  {row.appointment_source === "insider" ? "Insider" : "Outsider"}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-xl-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom">
                    <h6 className="mb-0">
                      <i className="bi bi-clock-history me-2"></i>Upcoming Appointments
                    </h6>
                  </div>
                  <div className="card-body">
                    {upcomingAppointments.length === 0 ? (
                      <p className="text-muted mb-0">No upcoming appointments.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th>Client</th>
                              <th>Slot</th>
                              <th>Source</th>
                            </tr>
                          </thead>
                          <tbody>
                            {upcomingAppointments.map((row) => (
                              <tr key={row.id}>
                                <td>{row.appointment_date ? moment(row.appointment_date).format("DD/MM/YY") : "—"}</td>
                                <td>{row.client_name || "—"}</td>
                                <td>{slotRange(row)}</td>
                                <td className="text-capitalize">{row.appointment_source || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom">
                    <h6 className="mb-0">
                      <i className="bi bi-clipboard2-pulse me-2"></i>Recent Prescriptions
                    </h6>
                  </div>
                  <div className="card-body">
                    {recentPrescriptions.length === 0 ? (
                      <p className="text-muted mb-0">No prescription records yet.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th>Client</th>
                              <th>Diagnosis</th>
                              <th>Region</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPrescriptions.map((row) => (
                              <tr key={row.id}>
                                <td>{row.prescription_date ? moment(row.prescription_date).format("DD/MM/YY") : "—"}</td>
                                <td>{row.client_name || "—"}</td>
                                <td>{row.diagnosis || "—"}</td>
                                <td>{row.region || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header bg-transparent border-bottom">
                <h6 className="mb-0">
                  <i className="bi bi-lightning-fill me-2"></i>Quick Actions
                </h6>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6 col-md-2">
                    <Link to="/physio/appointments" className="btn btn-outline-primary w-100">
                      Appointments
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <Link to="/physio/prescriptions" className="btn btn-outline-secondary w-100">
                      Prescriptions
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <Link to="/physio/gx" className="btn btn-outline-info w-100">
                      GX
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <Link to="/physio/daily-referrals" className="btn btn-outline-warning w-100">
                      Referrals
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <Link to="/physio/patient-details" className="btn btn-outline-dark w-100">
                      Patient Details
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <Link to="/physio/assessments" className="btn btn-outline-danger w-100">
                      Assessments
                    </Link>
                  </div>
                  <div className="col-6 col-md-2">
                    <button className="btn btn-outline-success w-100" onClick={fetchDashboard}>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhysioDashboard;
